/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './PageHeader';

const name = 'Page Header';

const PageHeaderManager: ComponentManagerFn<'PageHeader'> = (
  platform: Platform,
): ComponentManager<'PageHeader'> => {
  return {
    ...baseComponentManager<'PageHeader'>(platform, 'PageHeader'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) => {
      if (
        !(['Box', 'FlexPanel', 'Toolbar'] as ItemType[]).includes(childType)
      ) {
        return false;
      }
      return item.children.length === 0
        ? true
        : `Only one child component is allowed inside ${name}`;
    },
    props: {
      pageHeaderHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 100,
        step: 4,
      },
      disableAppHeaderTextColor: {
        label: 'Disable App Header Text Color',
        type: 'boolean',
        helpText:
          'Select this property to disable the header text color from Application Settings.',
      },
    },
    category: 'Page Header',
    name,
    excludeChildren: true,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'window-alt' },
    noOverlay: true,
    description: `**Overview**<br/>
The Page Header component is a structural container used to build a **Custom Header** for your page, replacing the standard platform application header. By using this component, you gain full control over the top navigation area's layout and content, allowing you to design bespoke branding, navigation, or action bars.
Use this component when the default application header does not meet your specific design requirements or when you need to embed custom widgets in a specific arrangement.

**1. Configuration**<br/>
* **Height:** Defines the fixed vertical height of the header area (0 to 100 pixels). This ensures the page content below it is pushed down appropriately to prevent overlapping.
* **Disable App Header Text Color:** By default, custom headers may inherit text color settings from global Application Settings. Checking this box allows you to override those defaults, giving you the freedom to style the text and icons within your header independently of the app theme.

**2. Layout & Children**<br/>
The Page Header acts as a structural shell, but it has specific constraints to ensure it functions correctly as a persistent navigation region.
* **Single Child Policy:** The component allows only **one direct child**. This structure forces the use of a layout container as the root element to organize multiple items.
* **Allowed Root Children:** You must use one of the following containers as the direct child:
    * **FlexPanel (Default):** Ideal for distributing items in a row (e.g., Logo on the left, Menu in the center, User Profile on the right).
    * **Box:** For simple, singular blocking or grouping.
    * **Toolbar:** For standard sets of action buttons.

**3. Usage Strategy**<br/>
To build a functional header, the standard design pattern is as follows:
1. Add the **Page Header**.
2. Inside it, add a **FlexPanel** set to "Row" direction.
3. Inside the FlexPanel, add all your individual header elements:
    * **Logo:** Image or Typography component.
    * **Navigation:** BoxList or Menu components.
    * **Search Bar:** SearchForm component.
    * **Utilities:** Active Users, Dark Mode Toggle, or specialized Buttons.

**4. Best Use Cases**<br/>
* **Branding:** Creating a header that strictly adheres to corporate style guides (specific colors, fonts, and logo placement) that differ from the standard application theme.
* **Operational Dashboards:** Designing a slim, data-dense header that maximizes vertical space for monitoring screens.
* **Landing Pages:** Removing standard global navigation to focus the user's attention on specific call-to-action buttons.
* **Multi-Tenant Apps:** Dynamically changing the header layout or logo based on the user's organization or role.`,
  };
};

export default PageHeaderManager;
